// ============================================================
// Liste des commerces + formulaire de création — Lot L04
// ============================================================
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import superAdminApi from '../../services/superAdminApi';
import { Plus, Building2, Users, Store, AlertCircle, Pause, Play, Archive } from 'lucide-react';
import toast from 'react-hot-toast';

const STATUT_COULEURS = {
  actif:    'bg-emerald-50 text-emerald-700 border-emerald-200',
  suspendu: 'bg-amber-50 text-amber-700 border-amber-200',
  archive:  'bg-slate-200 text-slate-500 border-slate-300',
};

const COULEURS_FOND = [
  '#1e3a8a', '#1e40af', '#1d4ed8', '#172554',
  '#0c4a6e', '#075985', '#082f49', '#0e7490',
  '#0f172a', '#1e293b', '#334155', '#020617',
  '#111827', '#1f2937', '#374151', '#030712',
  '#312e81', '#3730a3', '#4c1d95', '#581c87',
  '#6b21a8', '#86198f', '#701a75', '#4a044e',
  '#064e3b', '#065f46', '#022c22', '#14532d',
  '#166534', '#365314',
  '#7f1d1d', '#831843', '#9f1239', '#4c0519',
  '#7c2d12', '#9a3412', '#451a03', '#78350f',
];
const COULEURS_ICONES = [
  '#64748b', '#94a3b8', '#cbd5e1', '#e2e8f0', '#475569', '#334155',
  '#fbbf24', '#f59e0b', '#facc15', '#eab308', '#ca8a04',
  '#34d399', '#10b981', '#22c55e', '#84cc16', '#a3e635',
  '#60a5fa', '#3b82f6', '#0ea5e9', '#06b6d4', '#22d3ee',
  '#f472b6', '#ec4899', '#e879f9', '#d946ef',
  '#fb923c', '#f97316', '#ea580c',
  '#a78bfa', '#8b5cf6', '#7c3aed', '#a855f7',
  '#f87171', '#ef4444', '#dc2626',
];

function ModalCreer({ onClose, onSuccess }) {
  const [form, setForm] = useState({
    nom: '', ville: '', pays: 'Guinée',
    telephone: '', email: '', devise: 'GNF',
    langue: 'fr', type_activite: '',
    couleur_principale: '#1e3a8a',  // bleu marine
    couleur_secondaire: '#64748b',  // gris ardoise
    logo_url: '', max_boutiques: 1, adresse: '',
    essai_jours: 30,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Templates de secteur : pré-remplissent catégories + axes de variantes
  const [templates, setTemplates] = useState([]);
  useEffect(() => {
    superAdminApi.get('/superadmin/commerces/templates-secteurs')
      .then((r) => setTemplates(r.data || []))
      .catch(() => {});
  }, []);

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const payload = {
        ...form,
        max_boutiques: parseInt(form.max_boutiques) || 1,
        essai_jours: parseInt(form.essai_jours) || 30,
      };
      const r = await superAdminApi.post('/superadmin/commerces', payload);
      toast.success('Commerce créé');
      onSuccess(r.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur création');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <form onSubmit={handleSubmit} className="bg-white border border-slate-200 rounded-2xl w-full max-w-2xl p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <Building2 size={18} className="text-blue-600" /> Nouveau commerce
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <label className="block text-xs text-slate-500 mb-1">Nom du commerce <span className="text-red-600">*</span></label>
            <input name="nom" value={form.nom} onChange={handleChange} required autoFocus
              className="w-full bg-slate-100 border border-slate-300 text-slate-900 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-600" />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Ville</label>
            <input name="ville" value={form.ville} onChange={handleChange}
              className="w-full bg-slate-100 border border-slate-300 text-slate-900 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-600" />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Pays</label>
            <input name="pays" value={form.pays} onChange={handleChange}
              className="w-full bg-slate-100 border border-slate-300 text-slate-900 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-600" />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Téléphone</label>
            <input name="telephone" value={form.telephone} onChange={handleChange}
              className="w-full bg-slate-100 border border-slate-300 text-slate-900 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-600" />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Email</label>
            <input name="email" type="email" value={form.email} onChange={handleChange}
              placeholder="email@gmail.com"
              className="w-full bg-slate-100 border border-slate-300 text-slate-900 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-600" />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Secteur d'activité</label>
            <select name="type_activite" value={form.type_activite} onChange={handleChange}
              className="w-full bg-slate-100 border border-slate-300 text-slate-900 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-600">
              <option value="">— Aucun template —</option>
              {templates.map((t) => (
                <option key={t.code} value={t.code}>
                  {t.nom}{t.nb_categories > 0 ? ` (${t.nb_categories} catégories)` : ''}
                </option>
              ))}
            </select>
            <p className="text-[10px] text-slate-400 mt-1">
              Pré-remplit les catégories et la config des variantes du commerce.
            </p>
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs text-slate-500 mb-1">Adresse</label>
            <textarea name="adresse" value={form.adresse} onChange={handleChange} rows={2}
              className="w-full bg-slate-100 border border-slate-300 text-slate-900 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-600" />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Devise</label>
            <select name="devise" value={form.devise} onChange={handleChange}
              className="w-full bg-slate-100 border border-slate-300 text-slate-900 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-600">
              <option value="GNF">GNF (Franc Guinéen)</option>
              <option value="XOF">XOF (FCFA Ouest)</option>
              <option value="XAF">XAF (FCFA Central)</option>
              <option value="EUR">EUR</option>
              <option value="USD">USD</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Langue</label>
            <select name="langue" value={form.langue} onChange={handleChange}
              className="w-full bg-slate-100 border border-slate-300 text-slate-900 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-600">
              <option value="fr">Français</option>
              <option value="en">English</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Nb boutiques max</label>
            <input name="max_boutiques" type="number" min="1" value={form.max_boutiques} onChange={handleChange}
              className="w-full bg-slate-100 border border-slate-300 text-slate-900 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-600" />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Durée d'essai (jours)</label>
            <input name="essai_jours" type="number" min="1" max="3650" value={form.essai_jours} onChange={handleChange}
              className="w-full bg-slate-100 border border-slate-300 text-slate-900 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-500" />
            <p className="text-xs text-slate-400 mt-1">Le commerce démarre en essai gratuit. Prolongeable ensuite.</p>
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs text-slate-500 mb-1">Couleur de fond <span className="text-slate-400">(sidebar / header)</span></label>
            <div className="flex flex-wrap items-center gap-2">
              <input type="color" name="couleur_principale" value={form.couleur_principale} onChange={handleChange}
                className="w-12 h-9 rounded-lg cursor-pointer bg-slate-100 border border-slate-300" />
              <input value={form.couleur_principale} onChange={handleChange} name="couleur_principale"
                className="w-32 bg-slate-100 border border-slate-300 text-slate-900 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:border-blue-600" />
            </div>
            <div className="grid grid-cols-12 gap-1.5 mt-2 max-w-xl">
              {COULEURS_FOND.map(c => {
                const actif = (form.couleur_principale || '').toLowerCase() === c.toLowerCase();
                return (
                  <button key={c} type="button"
                    onClick={() => setForm(f => ({ ...f, couleur_principale: c }))}
                    className={`w-6 h-6 rounded-md border-2 hover:scale-110 transition-transform ${
                      actif ? 'border-blue-600 ring-2 ring-blue-400/40' : 'border-slate-300'
                    }`}
                    style={{ backgroundColor: c }} title={c} />
                );
              })}
            </div>
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs text-slate-500 mb-1">Couleur des icônes <span className="text-slate-400">(accents)</span></label>
            <div className="flex flex-wrap items-center gap-2">
              <input type="color" name="couleur_secondaire" value={form.couleur_secondaire} onChange={handleChange}
                className="w-12 h-9 rounded-lg cursor-pointer bg-slate-100 border border-slate-300" />
              <input value={form.couleur_secondaire} onChange={handleChange} name="couleur_secondaire"
                className="w-32 bg-slate-100 border border-slate-300 text-slate-900 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:border-blue-600" />
            </div>
            <div className="grid grid-cols-12 gap-1.5 mt-2 max-w-xl">
              {COULEURS_ICONES.map(c => {
                const actif = (form.couleur_secondaire || '').toLowerCase() === c.toLowerCase();
                return (
                  <button key={c} type="button"
                    onClick={() => setForm(f => ({ ...f, couleur_secondaire: c }))}
                    className={`w-6 h-6 rounded-md border-2 hover:scale-110 transition-transform ${
                      actif ? 'border-blue-600 ring-2 ring-blue-400/40' : 'border-slate-300'
                    }`}
                    style={{ backgroundColor: c }} title={c} />
                );
              })}
            </div>
            <div className="mt-2 p-3 rounded-lg flex items-center gap-2"
              style={{ backgroundColor: form.couleur_principale }}>
              <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: form.couleur_secondaire + '40' }}>
                <span style={{ color: form.couleur_secondaire }}>★</span>
              </div>
              <span className="text-xs font-medium" style={{
                color: parseInt(form.couleur_principale.replace('#',''), 16) > 0x7f7f7f ? '#0f172a' : '#ffffff'
              }}>Aperçu sidebar — {form.nom || 'Commerce'}</span>
            </div>
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs text-slate-500 mb-1">URL du logo (optionnel)</label>
            <input name="logo_url" type="url" value={form.logo_url} onChange={handleChange}
              placeholder="https://…/logo.png"
              className="w-full bg-slate-100 border border-slate-300 text-slate-900 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-600" />
            <p className="text-xs text-slate-400 mt-1">
              Le propriétaire pourra téléverser un logo depuis son ordinateur après la création.
            </p>
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">
            <AlertCircle size={14} /> {error}
          </div>
        )}

        <div className="flex gap-2 pt-2">
          <button type="button" onClick={onClose}
            className="flex-1 bg-slate-100 text-slate-900 px-4 py-2 rounded-lg text-sm font-medium hover:bg-slate-200">
            Annuler
          </button>
          <button type="submit" disabled={saving}
            className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
            {saving ? 'Création…' : 'Créer'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function Commerces() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creatingDemo, setCreatingDemo] = useState(false);
  const [demoResult, setDemoResult] = useState(null);

  const load = () => {
    setLoading(true);
    superAdminApi.get('/superadmin/commerces')
      .then((r) => setItems(r.data))
      .catch((e) => toast.error(e.response?.data?.message || 'Erreur chargement'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const creerDemo = async () => {
    if (!window.confirm('Créer un nouveau commerce DÉMO complet (boutique, produits, fournisseurs, comptes + stock initial) ?')) return;
    setCreatingDemo(true);
    try {
      const { data } = await superAdminApi.post('/superadmin/commerces/creer-demo');
      setDemoResult(data);
      toast.success('Commerce démo créé');
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Erreur lors de la création du commerce démo');
    } finally {
      setCreatingDemo(false);
    }
  };

  const changeStatut = async (id, statut) => {
    const motif = statut === 'actif' ? null : window.prompt(`Motif (${statut}) :`) || null;
    try {
      await superAdminApi.patch(`/superadmin/commerces/${id}/statut`, { statut, motif });
      toast.success(`Statut → ${statut}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Erreur');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Commerces</h1>
          <p className="text-sm text-slate-400">{items.length} client{items.length > 1 ? 's' : ''} sur la plateforme</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={creerDemo}
            disabled={creatingDemo}
            className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-medium"
            title="Créer un commerce de démonstration complet pour les tests"
          >
            <Store size={16} /> {creatingDemo ? 'Création…' : 'Créer un commerce démo'}
          </button>
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
          >
            <Plus size={16} /> Nouveau commerce
          </button>
        </div>
      </div>

      {loading ? (
        <p className="text-slate-400 text-sm">Chargement…</p>
      ) : items.length === 0 ? (
        <div className="border-2 border-dashed border-slate-200 rounded-xl p-12 text-center">
          <Building2 size={32} className="text-slate-400 mx-auto mb-3" />
          <p className="text-slate-500 text-sm">Aucun commerce pour l'instant.</p>
          <p className="text-slate-400 text-xs mt-1">Crée le premier client avec le bouton ci-dessus.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((c) => (
            <div key={c.id} className="bg-white border border-slate-200 rounded-xl p-4 hover:border-slate-300 transition-colors">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0 flex-1">
                  <Link to={`/superadmin/commerces/${c.id}`} className="text-slate-900 font-medium hover:text-blue-600 truncate block">
                    {c.nom}
                  </Link>
                  <p className="text-xs text-slate-400 truncate">{c.slug} · {c.devise}</p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUT_COULEURS[c.statut] || ''}`}>
                    {c.statut}
                  </span>
                  {c.demandes_en_attente > 0 && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium whitespace-nowrap"
                      title="Demande d'abonnement en attente">
                      📨 {c.demandes_en_attente} demande{c.demandes_en_attente > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3 text-xs text-slate-400 mb-3">
                <span className="flex items-center gap-1"><Store size={12} /> {c.nb_boutiques} boutique{c.nb_boutiques > 1 ? 's' : ''}</span>
                <span className="flex items-center gap-1"><Users size={12} /> {c.nb_users} utilisateur{c.nb_users > 1 ? 's' : ''}</span>
              </div>
              <div className="flex items-center gap-2">
                <Link to={`/superadmin/commerces/${c.id}`}
                  className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-900 text-xs px-3 py-1.5 rounded-lg text-center">
                  Détail
                </Link>
                {c.statut === 'actif' ? (
                  <button onClick={() => changeStatut(c.id, 'suspendu')}
                    title="Suspendre"
                    className="bg-slate-100 hover:bg-amber-700/50 text-amber-700 p-1.5 rounded-lg">
                    <Pause size={14} />
                  </button>
                ) : c.statut === 'suspendu' ? (
                  <button onClick={() => changeStatut(c.id, 'actif')}
                    title="Réactiver"
                    className="bg-slate-100 hover:bg-emerald-700/50 text-emerald-700 p-1.5 rounded-lg">
                    <Play size={14} />
                  </button>
                ) : (
                  <button onClick={() => changeStatut(c.id, 'actif')}
                    title="Désarchiver"
                    className="bg-slate-100 hover:bg-emerald-700/50 text-emerald-700 p-1.5 rounded-lg">
                    <Play size={14} />
                  </button>
                )}
                {c.statut !== 'archive' && (
                  <button onClick={() => changeStatut(c.id, 'archive')}
                    title="Archiver"
                    className="bg-slate-100 hover:bg-slate-200 text-slate-500 p-1.5 rounded-lg">
                    <Archive size={14} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreate && (
        <ModalCreer
          onClose={() => setShowCreate(false)}
          onSuccess={() => { setShowCreate(false); load(); }}
        />
      )}

      {demoResult && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg shadow-2xl p-5 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-bold text-emerald-700 flex items-center gap-2">
              <Store size={18} /> Commerce démo créé
            </h2>
            <p className="text-sm text-slate-600 mt-1">{demoResult.message}</p>
            <div className="mt-3 bg-slate-50 border border-slate-200 rounded-lg p-3">
              <p className="text-xs text-slate-500 uppercase font-semibold mb-2">Identifiants de connexion (application cliente)</p>
              {(demoResult.identifiants || []).map((u, i) => (
                <div key={i} className="text-sm py-1 border-b border-slate-100 last:border-0">
                  <span className="font-semibold text-slate-700">{u.role}</span><br />
                  <span className="font-mono text-slate-800">{u.email}</span>
                  <span className="text-slate-400"> · </span>
                  <span className="font-mono text-slate-800">{u.motdepasse}</span>
                </div>
              ))}
              {demoResult.vendeur_terrain && (
                <p className="text-xs text-slate-500 mt-2">+ {demoResult.vendeur_terrain}</p>
              )}
            </div>
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2 mt-3">
              Note ces identifiants : le mot de passe n'est affiché qu'ici. Connecte-toi sur l'application cliente pour tester.
            </p>
            <button onClick={() => setDemoResult(null)}
              className="w-full mt-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-900 text-white font-semibold">
              J'ai noté — Fermer
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
